/* eslint-disable no-restricted-globals */
import {
  Dialog,
  LinearProgress,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  CircularProgress,
  AlertColor
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import { useState, useEffect } from 'react';
import {
  AlertDialog,
  DialogResult,
  DynamicFormFieldRenderer
} from 'src/components/analytics';
import ButtonWithSpinner from 'src/components/buttons/button-with-spinner';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import {
  IDynamicFormValue,
  FormField2,
  getValidationSchema,
  IMultiSelectControlField2,
  ISelectOption
} from 'src/lib';
import * as Yup from 'yup';
import { getNextValues } from 'src/components/filters';
import {
  createFieldToOptionsMap,
  getAutoFillValue
} from 'src/components/filters/utils/filter-form-utils';
import { AnyObject } from 'yup/lib/types';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import { SKUPopupTitle, SKUPopupType } from '../../enums/sku-popup-enum';
import {
  createNewSku,
  editSku,
  fetchMetadata
} from '../../store/innovation-slice-async-thunks';
import {
  closeSkuPop,
  setSkuFormFieldOptions,
  toggleDisableStateForSkuFormFields,
  updateFooter
} from '../../store/innovation-slice';
import { getFormattedSkuObject } from '../../utils/utils';
import SnackbarComponent from '../snackbar/snackbar';

function StyledLabel({ label }: { label: string }) {
  return (
    <Typography
      component='label'
      fontSize={10}
      fontWeight={400}
      textAlign='left'
    >
      {label}
    </Typography>
  );
}

/* eslint-disable no-param-reassign */
const getDefaultValueFromRow = (
  formFields: FormField2[],
  currSkuObj: IDynamicFormValue
) =>
  formFields.reduce((result, obj) => {
    /**
     * checking for dropdown input
     *    inside that checking for string/number type
     *    inside number checking if filter is 'implement_after' then prepending 'P' to it
     *
     */
    if (obj.control_type.includes('DROPDOWN')) {
      result[obj.name] = currSkuObj[obj.name]
        ? typeof currSkuObj[obj.name] === 'string'
          ? currSkuObj[obj.name]
              .split(',')
              .map((e) => ({ label: e.trim(), value: e.trim() }))
          : obj.name === 'implement_after'
          ? [currSkuObj[obj.name]].map((e) => ({
              label: `P${e}`,
              value: `P${e}`
            }))
          : [currSkuObj[obj.name]].map((e) => ({ label: e, value: e }))
        : [];
    } else {
      result[obj.name] = currSkuObj[obj.name];
    }
    return result;
  }, {});
/* eslint-enable no-param-reassign */

export default function AddSKUDialog({
  isCreateDialogOpen,
  setCreateDialog
}: {
  isCreateDialogOpen: boolean;
  setCreateDialog: (isSnackbarToOpen, snackbarText, snackbarType) => void;
}) {
  const appDispatch = useAppDispatch();
  const { moduleId, scenarioId, sourceId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });

  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success'
  });

  const [validationSchema, setValidationSchema] = useState(
    Yup.object().shape({})
  );
  //  local state for confirmation dialogue
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<IDynamicFormValue>({});
  const {
    fetchingMetaData,
    skuDetailsObj: formFields,
    selectedSkuDetails,
    skuFieldsCombinations,
    skuPopupType,
    isSkuCRUDLoading,
    appliedFilters,
    prevSkuName,
    moduleValue,
    skusUniqueList
  } = useAppSelector((state) => state.innovation);

  const skuForm = useFormik<IDynamicFormValue>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values);
    }
  });

  /**
   * Returns filtered fields for which validation is required
   * @param fields All fields in the form
   * @param ppgToggle The current value of toggle (new or existing ppg)
   * @returns Fields for which validation schema is required
   */
  const getFieldsForValidationSchema = (
    fields: FormField2[],
    ppgToggle: boolean
  ) =>
    fields.filter((field) => {
      if (field.name === 'ppg') {
        return ppgToggle
          ? field.control_type === 'TEXT'
          : field.control_type === 'SINGLESELECT-DROPDOWN';
      }
      return field;
    });

  /**
   * A function to get validation schema for formik
   * @param fields All fields
   * @param ppgToggle The current value of toggle (new or existing ppg)
   * @returns a Yup schema validation object for each of the field
   */

  const getValidationSchemaForFields = (fields: FormField2[]) =>
    fields
      .map((field) => {
        const fieldSchema = getValidationSchema(field) as {
          [key: string]: Yup.StringSchema<string, AnyObject, string>;
        };
        // schema for checking unqiue sku name
        if (field.name === 'sku') {
          fieldSchema[field.name] = fieldSchema[field.name].test({
            test: (value) => {
              if (value) {
                if (
                  skusUniqueList.length > 0 &&
                  skusUniqueList.includes(value.trimEnd())
                ) {
                  return false;
                }
                return true;
              }
              return true;
            },
            message: 'Duplicate SKU name'
          });
        }
        return fieldSchema;
      })
      .reduce(
        (prev, curr) => ({ ...prev, ...curr }),
        {} as { [key: string]: any }
      );

  const resetDropdownOptionsOfAllFields = (toggle: boolean) => {
    const defaultOptionsToFieldMap = createFieldToOptionsMap(
      getFieldsForValidationSchema(formFields, toggle),
      skuFieldsCombinations
    );
    appDispatch(setSkuFormFieldOptions(defaultOptionsToFieldMap));
  };

  // getting text fields items (for sku, ppg and objective)
  const textFieldItems = formFields.filter((ele) =>
    ['ppg', 'sku', 'ppgToggle', 'objective'].includes(ele.name)
  );

  //  diving items into 2 parts sku details and execution details
  const skuDetailsItems = formFields.filter(
    (ele) => ele.section === 'sku_details'
  );

  const executionDetailsItem = formFields.filter(
    (ele) => ele.section === 'execution_details'
  );

  const handleChange = (newValue: unknown, field: FormField2) => {
    if (field.name === 'ppgToggle') {
      skuForm.setFieldValue('ppg', newValue ? '' : [], true);

      setTimeout(() => {
        skuForm.validateForm();
      }, 300);

      // enabling/disabling sku fields
      appDispatch(
        toggleDisableStateForSkuFormFields({
          isExistingPpgSelected: newValue as boolean
        })
      );

      const schema = getValidationSchemaForFields(
        getFieldsForValidationSchema(formFields, newValue as boolean)
      );
      setValidationSchema(Yup.object().shape(schema));
      if (newValue) {
        // Resetting all the dropdown options when ppg is set to new ppg toggle
        resetDropdownOptionsOfAllFields(newValue as boolean);
      }
      skuForm.setFieldValue(field.name, newValue, true);
    } else if (
      field.name === 'ppg' &&
      field.control_type === 'SINGLESELECT-DROPDOWN'
    ) {
      const fieldsToAutoFill = formFields.filter(
        (formField) => formField.is_auto_fill
      );
      const autoFilledValuesMap = getAutoFillValue(
        skuFieldsCombinations,
        'ppg',
        (newValue as ISelectOption[])[0].value,
        fieldsToAutoFill.map((formField) => formField.name)
      );
      const newValues = fieldsToAutoFill.reduce((prev, curr) => {
        if (
          curr.control_type === 'MULTISELECT-DROPDOWN' ||
          curr.control_type === 'SINGLESELECT-DROPDOWN'
        ) {
          return {
            ...prev,
            [curr.name]:
              curr.control_type === 'MULTISELECT-DROPDOWN'
                ? (
                    autoFilledValuesMap[curr.name] as string[]
                  ).map<ISelectOption>((value) => ({ label: value, value }))
                : (autoFilledValuesMap[curr.name] as string[])
                    .map<ISelectOption>((value) => ({ label: value, value }))
                    .slice(0, 1)
          };
        }
        return {
          ...prev,
          [curr.name]: (autoFilledValuesMap[curr.name] as any[])[0]
        };
      }, {});
      skuForm.setValues({
        ...skuForm.values,
        ...newValues,
        [field.name]: newValue
      });
    } else if (field.is_hierarchical && field.name !== 'ppg') {
      const { fieldNameToOptionsMap, newFormValues } = getNextValues(
        field.name,
        newValue as ISelectOption[],
        getFieldsForValidationSchema(formFields, skuForm.values.ppgToggle),
        skuFieldsCombinations,
        skuForm.values
      );
      appDispatch(setSkuFormFieldOptions(fieldNameToOptionsMap));
      skuForm.setValues(newFormValues);
    } else {
      skuForm.setFieldValue(field.name, newValue, true);
    }
  };

  const handleCloseOnIconClick = () => {
    setConfirmationDialogOpen(true);
  };

  const handleClose = (
    event,
    reason,
    isSnackbarToOpen,
    snackbarText = '',
    snackbarType = ''
  ) => {
    if (reason && reason === 'backdropClick') return;
    setCreateDialog(isSnackbarToOpen, snackbarText, snackbarType);
    appDispatch(closeSkuPop(false));
  };

  // function to handle confirmation dialogue close
  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES) {
      handleClose(event, 'reason', false);
    }
    setConfirmationDialogOpen(false);
  };

  // function to close the add/edit sku modal
  const closeModal = (currResponse, currentOperation) => {
    if (currResponse.meta.requestStatus === 'rejected') {
      setSnackbarContent({
        // eslint-disable-next-line dot-notation
        snackbarText: currResponse['error'].message,
        snackbarType: 'error'
      });
      setSnackbar(true);
    } else {
      // eslint-disable-next-line no-restricted-globals
      handleClose(
        event,
        'reason',
        true,
        `${
          currResponse.payload.row.length > 0
            ? currResponse.payload.row[0].parent.data.sku
            : 'sku'
        } ${currentOperation} successfully`,
        'success'
      );
    }
  };

  /**
   * function to submit the newly created and sku and add in to the innovation table
   */
  const addNewSku = () => {
    if (skuPopupType === SKUPopupType.CREATE) {
      return appDispatch(
        createNewSku({
          sourceID: sourceId,
          moduleID: moduleId,
          scenarioID: Number(scenarioId),
          newskus: [getFormattedSkuObject(skuForm.values, formFields)],
          filters: appliedFilters
        })
      ).then((res) => {
        closeModal(res, 'created');
      });
    }
    return appDispatch(
      editSku({
        sourceID: sourceId,
        moduleID: moduleId,
        scenarioID: Number(scenarioId),
        skuName: prevSkuName,
        editedSkuObj: getFormattedSkuObject(
          skuForm.values,
          formFields,
          moduleValue
        ),
        filters: appliedFilters
      })
    ).then((res) => {
      // after edit sku, updating the footer
      appDispatch(updateFooter());
      closeModal(res, 'modified');
    });
  };

  useEffect(() => {
    appDispatch(
      fetchMetadata({
        sourceID: sourceId,
        moduleID: moduleId,
        scenarioID: scenarioId,
        filters: appliedFilters
      })
    ).then((response) => {
      const { fields } = response.payload as { fields: FormField2[] };
      const ppgToggle =
        skuPopupType === SKUPopupType.CREATE
          ? false
          : !fields.some(
              (field) =>
                field.name === 'ppg' &&
                field.control_type === 'SINGLESELECT-DROPDOWN' &&
                (field as IMultiSelectControlField2).values.some(
                  (value) => value.value === selectedSkuDetails.ppg
                )
            );
      appDispatch(
        toggleDisableStateForSkuFormFields({
          isExistingPpgSelected: ppgToggle
        })
      );
      const commonDefaults =
        skuPopupType === SKUPopupType.CREATE
          ? fields.reduce<IDynamicFormValue>(
              (acc, curr) => ({
                ...acc,
                [curr.name]: curr.default_value
              }),
              {}
            )
          : getDefaultValueFromRow(
              fields.filter((field) => {
                if (field.name === 'ppg') {
                  return ppgToggle
                    ? field.control_type === 'TEXT'
                    : field.control_type === 'SINGLESELECT-DROPDOWN';
                }
                return field;
              }),
              selectedSkuDetails
            );
      const defaultValues = {
        ...commonDefaults,
        ppgToggle,
        retailer: [{ label: 'All', value: 'All' }]
      };
      const schema = getValidationSchemaForFields(
        getFieldsForValidationSchema(fields, defaultValues.ppgToggle)
      );
      setValidationSchema(Yup.object().shape(schema));
      setInitialValues(defaultValues);
    });
  }, []);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='lg'
        open={isCreateDialogOpen}
        // onClose={(event) => handleClose(event, 'reason', false)}
        disableEscapeKeyDown
      >
        <DialogTitle id='alert-dialog-title' px={2}>
          {SKUPopupTitle[skuPopupType]}
          <IconButton
            aria-label='close'
            onClick={handleCloseOnIconClick}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '20px 28px' }}>
          {fetchingMetaData ? (
            <Grid
              container
              spacing={1}
              alignItems='center'
              justifyContent='center'
              mt={1}
            >
              <CircularProgress />
            </Grid>
          ) : (
            <Grid container spacing={1}>
              {/* section for input text box of sku, ppg and objective */}
              <Grid container spacing={1} mb={1} mt={0.5} alignItems='center'>
                {textFieldItems
                  .filter((field) => field.name === 'sku')
                  .map((field) => (
                    <Grid item xs={6} sx={{ position: 'relative', top: '7px' }}>
                      <StyledLabel label={field.alias} />
                      <DynamicFormFieldRenderer
                        form={skuForm}
                        handleChange={(value) => handleChange(value, field)}
                        field={field}
                      />
                    </Grid>
                  ))}
                <Grid item xs={6}>
                  {textFieldItems
                    .filter((field) => field.name === 'ppgToggle')
                    .map((field) => (
                      <Grid
                      // sx={{
                      //   position: 'relative',
                      //   top: !skuForm.values?.ppgToggle ? '-4px' : '-2px'
                      // }}
                      >
                        <DynamicFormFieldRenderer
                          form={skuForm}
                          handleChange={(value) => handleChange(value, field)}
                          field={field}
                        />
                      </Grid>
                    ))}
                  {textFieldItems
                    .filter((field) => field.name === 'ppg')
                    .filter((field) =>
                      skuForm.values.ppgToggle
                        ? field.control_type === 'TEXT'
                        : field.control_type === 'SINGLESELECT-DROPDOWN'
                    )
                    .map((field) => (
                      <Grid>
                        <DynamicFormFieldRenderer
                          form={skuForm}
                          handleChange={(value) => handleChange(value, field)}
                          field={field}
                        />
                      </Grid>
                    ))}
                </Grid>
                {textFieldItems
                  .filter((field) => field.name === 'objective')
                  .map((field) => (
                    <Grid item xs={12}>
                      <StyledLabel label={field.alias} />
                      <DynamicFormFieldRenderer
                        form={skuForm}
                        handleChange={(value) => handleChange(value, field)}
                        field={field}
                      />
                    </Grid>
                  ))}
              </Grid>

              {/* section for input fields for sku and execution details */}
              <Grid container spacing={1} mt={1} borderTop='1px solid #DCDCDC'>
                {/* sku and execution title */}
                <Grid container spacing={1} mt={1}>
                  <Grid item xs={6}>
                    <Typography className='input-field-title'>
                      SKU Details
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className='input-field-title'>
                      Execution Details
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={6}
                    borderRight='1px solid #DCDCDC'
                    paddingRight='8px'
                  >
                    <Grid container spacing={1} alignItems='center'>
                      {skuDetailsItems
                        .filter((field) => field.is_visible)
                        .map((field) => (
                          <Grid item xs={4}>
                            <StyledLabel label={field.alias} />
                            <DynamicFormFieldRenderer
                              form={skuForm}
                              handleChange={(value) =>
                                handleChange(value, field)
                              }
                              field={field}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={6} paddingLeft='8px'>
                    <Grid container spacing={1} alignItems='center'>
                      {executionDetailsItem
                        .filter((field) => field.is_visible)
                        .map((field) => (
                          <Grid item xs={4}>
                            <StyledLabel label={field.alias} />
                            <DynamicFormFieldRenderer
                              form={skuForm}
                              handleChange={(value) =>
                                handleChange(value, field)
                              }
                              field={field}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          <AlertDialog
            closeHandler={handleConfirmationDialogClose}
            isDialogOpen={isConfirmationDialogOpen}
            message='Inputs will be reset and all changes to will be lost. Continue?'
            title='Cancel'
          />
        </DialogContent>
        <DialogActions
          sx={{ boxShadow: '0px -2.84583px 2.84583px rgba(0, 0, 0, 0.04)' }}
        >
          <Button
            disabled={isSkuCRUDLoading}
            variant='outlined'
            onClick={() => {
              resetDropdownOptionsOfAllFields(skuForm.values.ppgToggle);
              appDispatch(
                toggleDisableStateForSkuFormFields({
                  isExistingPpgSelected: initialValues.ppgToggle
                })
              );
              const schema = getValidationSchemaForFields(
                getFieldsForValidationSchema(
                  formFields,
                  initialValues.ppgToggle
                )
              );
              setValidationSchema(Yup.object().shape(schema));
              skuForm.resetForm();

              setTimeout(() => {
                Object.keys(skuForm.values).forEach((e: any) => {
                  skuForm.setFieldTouched(e, true, true);
                });
              }, 300);

              setTimeout(() => {
                skuForm.validateForm();
              }, 300);
            }}
          >
            Reset
          </Button>
          <ButtonWithSpinner
            disabled={!skuForm.isValid || fetchingMetaData}
            onClick={addNewSku}
          >
            Submit
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />
    </>
  );
}
